import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { Trash2, Plus, Loader2 } from 'lucide-react';

type Item = { id: string; message: string; active: boolean; created_at: string };

export default function AdminAnnouncements() {
  const { toast } = useToast();
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  const load = async () => {
    setLoading(true);
    const { data } = await supabase.from('announcements').select('*').order('created_at', { ascending: false });
    setItems((data as Item[]) || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!message.trim()) return;
    const { error } = await supabase.from('announcements').insert({ message: message.trim() });
    if (error) toast({ title: 'Failed', description: error.message, variant: 'destructive' });
    else { setMessage(''); toast({ title: 'Announcement posted' }); load(); }
  };

  const toggle = async (id: string, active: boolean) => {
    await supabase.from('announcements').update({ active }).eq('id', id);
    setItems((prev) => prev.map((x) => x.id === id ? { ...x, active } : x));
  };

  const remove = async (id: string) => {
    await supabase.from('announcements').delete().eq('id', id);
    toast({ title: 'Removed' });
    load();
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">Announcements</h2>
      <div className="rounded-lg border bg-card p-4 space-y-3">
        <h3 className="font-medium text-sm">New announcement</h3>
        <Textarea placeholder="e.g., Maintenance window tonight at 10 PM" value={message} onChange={(e) => setMessage(e.target.value)} />
        <Button onClick={add}><Plus className="h-4 w-4 mr-1" />Post</Button>
      </div>
      {loading ? (
        <div className="text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading...</div>
      ) : items.length === 0 ? (
        <div className="text-muted-foreground text-sm">No announcements yet.</div>
      ) : (
        <div className="space-y-2">
          {items.map((i) => (
            <div key={i.id} className="rounded-lg border bg-card p-4 flex items-start gap-3">
              <Switch checked={i.active} onCheckedChange={(v) => toggle(i.id, v)} />
              <div className="flex-1">
                <p className="text-sm">{i.message}</p>
                <p className="text-xs text-muted-foreground mt-1">{new Date(i.created_at).toLocaleString()}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => remove(i.id)}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}